import React, { ReactElement, useMemo } from 'react';
import classnames from 'classnames';
import PriceComparison from '../../menu/PriceComparison';
import { useTranslation } from 'react-i18next';
import useProductLikes from '../../../hooks/global/useProductLikes';
import useProductIdentifiers from '../../../hooks/menu/useProductIdentifiers';
import SeoResource from '../../../services/resources/SeoResource';
import HeartIcon from '../../../assets/logo/HeartIcon';
import useMenu from '../../../hooks/menu/useMenu';
import useBasket from '../../../hooks/basket/useBasket';
import ThumbUpIcon from '../../../modules/mobile/assets/icons/ThumbUpIcon';
import PlusIcon from '../../../assets/logo/PlusIcon';
import useTheme from '../../../hooks/ui/useTheme';
import { WithActualPrice } from '../../../providers/MenuProvider';
import { ProductInterface } from '../../../services/exports/Interfaces';
import useStorefront from '../../../hooks/storefront/useStorefront';
import useScreenType from '../../../hooks/utility/useScreenType';
import useFreeItemOffer from '../../../features/offers/hooks/useFreeItemOffer';
import useBogoItemOffer from '../../../features/offers/hooks/useBogoItemOffer';
import useOffersStuff from '../../../features/offers/hooks/useOffersStuff';
import useGetMenuOfferHandlerByKind from '../../../features/offers/hooks/get-offer-handler/useGetMenuOfferHandlerByKind';
import { OfferKindEnum } from '../../../services/exports/Constants';

interface Props {
  product: WithActualPrice<ProductInterface>;
  discount?: string | number;
  addedQuantity?: number;
  toggleProduct?: () => void;
  className?: string;
}

const MultiSelectProduct = (props: Props): ReactElement => {
  const { product } = props;
  const { isDesktop } = useScreenType();
  const { items } = useBasket();

  const addedQuantity = useMemo(
    () => items.reduce((prev, curr) => (curr.product_id === product.id ? prev + curr.quantity : prev), 0),
    [items],
  );

  const config = {
    ...props,
    addedQuantity,
  };

  return isDesktop ? <MultiSelectProduct.Web {...config} /> : <MultiSelectProduct.Mobile {...config} />;
};

export default MultiSelectProduct;

MultiSelectProduct.Web = (props: Props): ReactElement => {
  const { product, addedQuantity, toggleProduct } = props;
  const { productHeight } = useMenu();
  const { isStorefront } = useStorefront();

  return (
    <article
      className={classnames(
        'w-full flex flex-row select-none relative bg-brand-card overflow-hidden border rounded-2xl cursor-pointer',
        {
          '!rounded-[8px]': isStorefront,
        },
      )}
      style={{ height: productHeight }}
      onClick={() => toggleProduct && toggleProduct()}
    >
      <MultiSelectProduct.LeftColumn product={product} isMobile={false} />
      <MultiSelectProduct.RightColumn product={product} addedQuantity={addedQuantity} />
      <MultiSelectProduct.SoldOutOverlay product={product} />
    </article>
  );
};

MultiSelectProduct.Mobile = ({ product, addedQuantity, toggleProduct }: Props): ReactElement => {
  const { productHeight } = useMenu();

  return (
    <article
      className={classnames(
        'w-full flex flex-row items-center select-none relative overflow-hidden gap-2 py-md cursor-pointer',
      )}
      style={{ height: productHeight }}
      onClick={() => toggleProduct && toggleProduct()}
    >
      <MultiSelectProduct.LeftColumn product={product} isMobile={true} />
      <MultiSelectProduct.RightColumn product={product} addedQuantity={addedQuantity} />
      <MultiSelectProduct.SoldOutOverlay product={product} />
    </article>
  );
};

MultiSelectProduct.SoldOutOverlay = ({ product }: { product: WithActualPrice<ProductInterface> }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:MultiSelectMenuItems' });

  if (product?.is_available) {
    return null;
  }

  return (
    <div className={classnames('top-0 left-0 absolute p h-full w-full z-10')}>
      <div className="flex flex-col items-center justify-center h-full rounded-xl bg-black bg-opacity-50">
        <div className="flex flex-col absolute inset-0 items-center justify-center">
          <p className="text-white text-center">{t('labels.sold_out')}</p>
        </div>
      </div>
    </div>
  );
};

MultiSelectProduct.PriceRow = ({ product }: { product: WithActualPrice<ProductInterface> }) => {
  const { toggleProductLike, isLiked } = useProductLikes();
  const liked = isLiked(product.id);
  const theme = useTheme();
  const originalPrice = product.base_price;

  return (
    <div className={'flex flex-row items-center w-full relative text-brand-text-secondary font-semibold'}>
      <div className="h-full flex flex-col justify-end">
        <PriceComparison
          actualPrice={product?.actual_price}
          originalPrice={originalPrice}
          actualPriceStyle={{
            fontWeight: 'semibold',
            color: theme.color.brand.text.primary,
            fontSize: 15,
          }}
          originalPriceStyle={{
            fontWeight: 'semibold',
            fontSize: 15,
          }}
        />
      </div>
      <div className="flex flex-row items-center">
        <div
          className="ml-xs flex items-center"
          onClick={(e) => {
            e.stopPropagation();
            toggleProductLike(product.id);
          }}
        >
          <HeartIcon
            filled={liked}
            className={classnames('h-3', {
              'fill-danger': liked,
              'fill-gray-700': !liked,
            })}
          />
          <span className="text-xs text-brand-text-secondary ml-2xs w-3">{product.likes_count}</span>
        </div>
      </div>
    </div>
  );
};

MultiSelectProduct.ProductImage = ({
  product,
  addedQuantity,
}: {
  product: WithActualPrice<ProductInterface>;
  addedQuantity: number;
}): JSX.Element => {
  const { isDesktop } = useScreenType();

  return (
    <div
      className={classnames('relative h-full', {
        'w-[120px] h-[120px]': !isDesktop,
      })}
    >
      <div
        className={classnames('flex h-full shrink-none overflow-hidden relative', {
          'lg:py-none lg:pr-none py-md pr-sm lg:rounded-none': isDesktop,
        })}
      >
        <div className={'relative h-full w-full my-auto aspect-square object-cover'}>
          <img
            src={product?.thumbnail_url}
            alt={SeoResource.toAltText(`${product?.name} image`)}
            className={'relative h-full w-full my-auto aspect-square object-cover lg:rounded-none rounded-lg'}
          />
        </div>
      </div>
      <MultiSelectProduct.AddButton addedQuantity={addedQuantity} />
    </div>
  );
};

MultiSelectProduct.AddButton = ({
  addedQuantity,
  className,
}: {
  addedQuantity: number;
  className?: string;
}): JSX.Element => {
  const theme = useTheme();
  if (addedQuantity) {
    return (
      <div
        className={classnames(
          'absolute lg:bottom-2 lg:right-2 bottom-1 right-1 bg-brand-card rounded-lg lg:w-9 lg:h-9 w-8 h-8 flex items-center justify-center border',
          className,
        )}
      >
        <h6
          className="text-sm text-brand-text-secondary font-medium"
          style={{
            lineHeight: 1,
          }}
        >
          {addedQuantity}
        </h6>
      </div>
    );
  }

  return (
    <div
      className={classnames(
        'absolute lg:bottom-2 lg:right-2 bottom-1 right-1 bg-brand-card rounded-lg lg:w-9 lg:h-9 w-8 h-8 flex items-center justify-center border border-brand-border',
        className,
      )}
    >
      <PlusIcon color={theme.color.brand.icon.color} className="!h-[12px] !w-[12px]" />
    </div>
  );
};

MultiSelectProduct.Labels = ({ product }: { product: WithActualPrice<ProductInterface> }) => {
  const { emojis } = useProductIdentifiers(product);

  return (
    <div className="flex flex-row items-center gap-x-2">
      {emojis.isNotEmpty() && <p className="text-right">{emojis.implode(' ')}</p>}
    </div>
  );
};

MultiSelectProduct.LeftColumn = ({
  product,
}: {
  product: WithActualPrice<ProductInterface>;
  toggleProduct?: () => void;
  isMobile: boolean;
}) => {
  const { isDesktop } = useScreenType();

  return (
    <div
      className={classnames('flex flex-col flex-1 shrink relative justify-between', {
        'py-md px-md': isDesktop,
      })}
    >
      <div className={'flex flex-row justify-between flex-1'}>
        <div className={'flex flex-1 flex-col justify-between lg:gap-y-sm gap-y-[2px]'}>
          <div className="flex flex-col  lg:space-y-[4px] space-y-[2px]">
            <MultiSelectProduct.BestSeller product={product} />
            <OfferBadge product={product} />
            <FreeItemOfferBadge product={product} />
            <BogoItemOfferBadge product={product} />
            <div className={classnames('flex flex-col space-y-xs', { '!space-y-[4px]': !isDesktop })}>
              <div className="relative">
                <h6
                  className={classnames('font-normal text-[15px] text-brand-text-primary line-clamp-1 break-words', {
                    'font-semibold': !isDesktop,
                  })}
                >
                  {product?.name}
                </h6>
              </div>
              <MultiSelectProduct.PriceRow product={product} />
              {product?.description && (
                <p
                  className={classnames('text-brand-text-secondary font-medium line-clamp-3 break-words text-xs', {
                    '!line-clamp-2': !isDesktop,
                  })}
                >
                  {product.description}
                </p>
              )}
            </div>
          </div>
          <MultiSelectProduct.Labels product={product} />
        </div>
      </div>
    </div>
  );
};

MultiSelectProduct.RightColumn = ({
  product,
  addedQuantity,
}: {
  product: WithActualPrice<ProductInterface>;
  addedQuantity: number;
}) => {
  if (!product?.thumbnail_url) {
    return (
      <div className={classnames('flex h-full w-[50px] overflow-hidden relative')}>
        <MultiSelectProduct.AddButton addedQuantity={addedQuantity} />
      </div>
    );
  }

  return <MultiSelectProduct.ProductImage product={product} addedQuantity={addedQuantity} />;
};

MultiSelectProduct.Discount = ({ discount }: { discount: string | number }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:Product:ProductItem' });
  if (!discount) {
    return null;
  }

  return (
    <p className="w-fit bg-brand-primary/30 text-xs p-1 rounded flex items-center justify-center text-brand-primary font-medium">
      {t('discount_badge', { value: discount })}
    </p>
  );
};

interface OfferBadgeProps {
  product: WithActualPrice<ProductInterface>;
}

const OfferBadge = ({ product }: OfferBadgeProps) => {
  const { appliedOffers } = useOffersStuff();
  const getMenuOfferHandlerByKind = useGetMenuOfferHandlerByKind();

  if (!appliedOffers?.length) {
    return null;
  }

  const handlers = appliedOffers
    .filter(
      (appliedOffer) =>
        ![OfferKindEnum.FreeItemByProducts, OfferKindEnum.Bogo, OfferKindEnum.BogoHalfPrice].includes(
          appliedOffer.kind,
        ),
    )
    .map(getMenuOfferHandlerByKind)
    .filter((handler) => handler?.isEligible && handler?.isEligible(product));

  if (!handlers.length) {
    return null;
  }

  return (
    <>
      {handlers.map((handler) => (
        <p
          className="w-fit bg-brand-primary/30 text-xs p-1 rounded flex items-center justify-center text-brand-primary font-medium"
          key={handler.badge}
        >
          {handler.badge}
        </p>
      ))}
    </>
  );
};

const FreeItemOfferBadge = ({ product }: OfferBadgeProps) => {
  const freeItemOffer = useFreeItemOffer();

  if (!freeItemOffer.isEligible || !freeItemOffer?.isEligible(product)) {
    return null;
  }

  return (
    <p className="w-fit bg-brand-primary/30 text-xs p-1 rounded flex items-center justify-center text-brand-primary font-medium">
      {freeItemOffer.badge}
    </p>
  );
};

const BogoItemOfferBadge = ({ product }: OfferBadgeProps) => {
  const bogoItemOffer = useBogoItemOffer();

  if (!bogoItemOffer.isEligible || !bogoItemOffer?.isEligible(product)) {
    return null;
  }

  return (
    <p className="w-fit bg-brand-primary/30 text-xs p-1 rounded flex items-center justify-center text-brand-primary font-medium">
      {bogoItemOffer.badge}
    </p>
  );
};

MultiSelectProduct.BestSeller = ({ product }: { product: WithActualPrice<ProductInterface> }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:MultiSelectMenuItems' });
  const theme = useTheme();

  if (!product.popularity_rank) {
    return null;
  }

  return (
    <div className="flex gap-x-1 items-center">
      <ThumbUpIcon color={theme.color.brand.primary} className="lg:h-5 lg:w-5 h-4 w-4" />
      <p className="lg:text-xs text-2xs font-medium">{t('labels.most_popular', { rank: product?.popularity_rank })}</p>
    </div>
  );
};
